/**
 * Editing an inline-conditional chip in place: clicking one (or Enter while it is selected) asks the
 * page to open ConditionalRunPopover for it, and applyConditionalRunEdit writes the result back as a
 * single transaction with its own history step.
 *
 * A branch is edited as text in template syntax — `Dr. {{title}}`, `{{amount | currency:USD}}` — and
 * parsed back into runs and placeholders. Only a branch made of text, fields, line breaks and tabs can
 * be edited that way; one holding anything else (a nested condition, a link, a page field) stays as it
 * is and the popover shows it read-only. An unchanged branch is kept verbatim, so opening and applying
 * never loses run formatting; a changed one takes the format of its first run.
 */
import { Extension, type Editor } from '@tiptap/core'
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state'
import { closeHistory } from '@tiptap/pm/history'
import { normalizeMonikerKey } from './monikerKey'
import { placeholderLabel } from './formatters'
import { placeholderNameError } from './placeholderEdit'
import { defaultRunFormat, defaultSourceLocation, type ConditionalRun, type DocInline, type FormatterSpec, type RunFormat } from './types'

type EditRequest = (pos: number) => void
const editRequests = new WeakMap<Editor, EditRequest>()

export function setConditionalRunEditHandler(editor: Editor, handler: EditRequest | null) {
  if (handler) editRequests.set(editor, handler)
  else editRequests.delete(editor)
}

/** The branch as template text, or null when it holds something text cannot express. */
export function branchToText(inlines: DocInline[]): string | null {
  let out = ''
  for (const i of inlines) {
    switch (i.$type) {
      case 'run': out += i.text; break
      case 'placeholder': out += placeholderLabel(i.rawName, i.formatters); break
      case 'break': out += '\n'; break
      case 'tab': out += '\t'; break
      default: return null
    }
  }
  return out
}

// ── Port of FormatterSyntax.Split (lib/src/Services/FormatterSyntax.cs) ─────────────────────────

function splitUnquoted(value: string, separator: string): string[] {
  const parts: string[] = []
  let cur = ''
  let quote = ''
  for (const ch of value) {
    if (quote) {
      cur += ch
      if (ch === quote) quote = ''
      continue
    }
    if (ch === "'" || ch === '"') { quote = ch; cur += ch; continue }
    if (ch === separator) { parts.push(cur); cur = ''; continue }
    cur += ch
  }
  parts.push(cur)
  return parts
}

function indexOfUnquoted(value: string, target: string): number {
  let quote = ''
  for (let i = 0; i < value.length; i++) {
    const ch = value[i]
    if (quote) { if (ch === quote) quote = ''; continue }
    if (ch === "'" || ch === '"') { quote = ch; continue }
    if (ch === target) return i
  }
  return -1
}

const unquote = (v: string) => (v.length >= 2 && (v[0] === "'" || v[0] === '"') && v[v.length - 1] === v[0] ? v.slice(1, -1) : v)

export function splitMoniker(inner: string): { key: string; formatters: FormatterSpec[] | null } {
  const parts = splitUnquoted(inner, '|')
  if (parts.length === 1) return { key: unquote(inner.trim()), formatters: null }
  const formatters: FormatterSpec[] = []
  for (const part of parts.slice(1)) {
    const spec = part.trim()
    if (!spec) continue
    const colon = indexOfUnquoted(spec, ':')
    if (colon < 0) { formatters.push({ name: spec, args: [] }); continue }
    const args = splitUnquoted(spec.slice(colon + 1), ',').map(a => unquote(a.trim())).filter(a => a.length > 0)
    formatters.push({ name: spec.slice(0, colon).trim(), args })
  }
  return { key: unquote(parts[0].trim()), formatters: formatters.length ? formatters : null }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Why the branch text cannot be used, or null. */
export function branchTextError(text: string): string | null {
  for (const m of text.matchAll(/\{\{([^{}]*)\}\}/g)) {
    const inner = m[1].trim()
    if (inner.startsWith('#') || inner.startsWith('/') || inner === 'else') return 'A condition or loop cannot be typed inside a branch.'
    const nameError = placeholderNameError(splitMoniker(inner).key)
    if (nameError) return `${m[0]}: ${nameError}`
  }
  return null
}

/** Parses branch text back into runs, placeholders, breaks and tabs, all in `format`. */
export function textToBranch(text: string, format: RunFormat): DocInline[] {
  const out: DocInline[] = []
  const pushText = (s: string) => {
    const pieces = s.split(/(\n|\t)/)
    for (const p of pieces) {
      if (p === '') continue
      if (p === '\n') out.push({ $type: 'break' })
      else if (p === '\t') out.push({ $type: 'tab' })
      else out.push({ $type: 'run', text: p, format: { ...format } })
    }
  }
  let last = 0
  for (const m of text.matchAll(/\{\{([^{}]*)\}\}/g)) {
    pushText(text.slice(last, m.index))
    const { key, formatters } = splitMoniker(m[1])
    out.push({ $type: 'placeholder', rawName: key, normalizedKey: normalizeMonikerKey(key), formatters, format: { ...format }, source: defaultSourceLocation() })
    last = m.index! + m[0].length
  }
  pushText(text.slice(last))
  return out
}

function branchFormat(inlines: DocInline[] | null): RunFormat {
  const first = inlines?.find(i => i.$type === 'run' || i.$type === 'placeholder') as { format: RunFormat } | undefined
  return first ? first.format : defaultRunFormat()
}

export interface ConditionalRunEdit {
  rawName: string
  negate: boolean
  /** Branch text; null keeps the branch as it is (one text cannot express). */
  text: string | null
  /** False drops the else branch. */
  hasElse: boolean
  elseText: string | null
}

/** Builds the edited node; branches whose text is unchanged are kept verbatim. */
export function editedConditionalRun(run: ConditionalRun, edit: ConditionalRunEdit): ConditionalRun {
  const rawName = edit.rawName.trim()
  const rebuild = (orig: DocInline[] | null, text: string | null, fallback: DocInline[] | null): DocInline[] => {
    if (text === null) return orig ?? []
    if (orig && branchToText(orig) === text) return orig
    return textToBranch(text, branchFormat(orig ?? fallback))
  }
  return {
    ...run,
    rawName,
    normalizedKey: normalizeMonikerKey(rawName),
    negate: edit.negate,
    inlines: rebuild(run.inlines, edit.text, null),
    elseInlines: edit.hasElse ? rebuild(run.elseInlines, edit.elseText ?? '', run.inlines) : null,
  }
}

export function applyConditionalRunEdit(editor: Editor, pos: number, edit: ConditionalRunEdit): boolean {
  const { state, view } = editor
  const node = state.doc.nodeAt(pos)
  if (!node || node.type.name !== 'conditionalRun' || placeholderNameError(edit.rawName)) return false
  if ((edit.text !== null && branchTextError(edit.text)) || (edit.hasElse && edit.elseText !== null && branchTextError(edit.elseText))) return false
  const run = node.attrs.json as ConditionalRun
  const next = editedConditionalRun(run, edit)
  if (JSON.stringify(next) === JSON.stringify(run)) return true
  const tr = closeHistory(state.tr)
  tr.setNodeMarkup(pos, undefined, { ...node.attrs, json: next })
  tr.setSelection(NodeSelection.create(tr.doc, pos))
  view.dispatch(tr)
  return true
}

export const ConditionalRunEditing = Extension.create({
  name: 'conditionalRunEditing',
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('conditionalRunEditing'),
        props: {
          handleClickOn(_view, _pos, node, nodePos, event) {
            if (node.type.name !== 'conditionalRun' || !editor.isEditable || event.button !== 0) return false
            const request = editRequests.get(editor)
            if (request) window.setTimeout(() => request(nodePos))
            return false
          },
          handleKeyDown(view, event) {
            if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return false
            const sel = view.state.selection
            if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'conditionalRun') return false
            const request = editRequests.get(editor)
            if (!request || !editor.isEditable) return false
            request(sel.from)
            return true
          },
        },
      }),
    ]
  },
})
